import React from 'react';
import styled from 'styled-components';
import { ink, inkOn, space, typeRole } from '@repo/design-tokens';
import { Card, Icon } from '@repo/design-system';
import { TagSwitcher } from '@repo/ui';
import type { TagSwitcherOption } from '@repo/ui';
import type { ObserverTrackSide } from '@repo/frontend-types';

import { TRACK_SIDE_OPTIONS, trackSideLabel } from '../observerSessionState';

/**
 * The six body parts, lavender only while chosen (v3). Manual activation: the arrows move focus and
 * Enter or Space chooses, because a choice on a selected observer is an edit on the undo history.
 */
const options: TagSwitcherOption[] = TRACK_SIDE_OPTIONS.map(side => ({
  value: side,
  text: trackSideLabel(side),
  tone: 'lavender',
}));

/**
 * The tracking card over the picture (`observers-page-camera-stage`, FR10; card 22): the six body
 * parts as lavender `Tag` toggles in ONE roving tab stop. It replaces the hand-rolled Track segments.
 * It does not drag itself: `MovableCard` moves it and hands it the `grip`, drawn first in its header
 * (`observers-page-motion`, v3 L1752).
 */
export const TrackingCard: React.FC<{
  grip?: React.ReactNode;
  title: string;
  value: ObserverTrackSide | null;
  disabled: boolean;
  onChange: (side: ObserverTrackSide) => void;
}> = ({ grip, title, value, disabled, onChange }) => {
  return (
    <Root surface="on-scene" data-control="">
      <Head>
        {grip}
        <span className="person" aria-hidden="true">
          <Icon name="person" variant="solid" size="xs" />
        </span>
        <Title>{title}</Title>
      </Head>
      <TagSwitcher
        label="Body part to track"
        size="dense"
        activation="manual"
        options={options}
        selectedValue={value ?? undefined}
        disabled={disabled}
        onChange={side => onChange(side as ObserverTrackSide)}
      />
    </Root>
  );
};

const Root = styled(Card)`
  display: grid;
  gap: ${space[2]};
  max-width: 360px;
  pointer-events: auto;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[2]};
  .person {
    color: ${inkOn.lav};
  }
`;

const Title = styled.span`
  ${typeRole.caption1Strong};
  color: ${ink[2]};
`;
